// Admin — Amenity Bookings list & management. Accessible to Super Admin,
// Editor, and Infrastructure roles. Actions per booking:
//   * View full details in a right-side sheet
//   * Verify a bank-transfer submission → CONFIRMED
//   * Reject a bank-transfer submission with a reason
//   * Cancel a booking
//   * Download the PDF receipt (for CONFIRMED only)
import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Loader2, Calendar as CalendarIcon, Search, CheckCircle2, XCircle,
  Ban, Download, Eye, ExternalLink, Plus, Undo2, IndianRupee, Info as InfoIcon, Pencil,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const inputCls = "bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#234333] focus:border-[#234333]";

const STATUSES = ["ALL", "PROVISIONAL", "PENDING_VERIFICATION", "CONFIRMED", "COMPLETED", "CANCELLED", "REJECTED", "FAILED_PAYMENT"];

const STATUS_BADGE = {
  PROVISIONAL: "bg-[#FEF7E4] text-[#8a5a1a] border-[#c78e42]/40",
  PENDING_VERIFICATION: "bg-[#FEF7E4] text-[#8a5a1a] border-[#c78e42]/40",
  CONFIRMED: "bg-[#E7F3F1] text-[#0D5C63] border-[#0D5C63]/30",
  COMPLETED: "bg-[#eef3ea] text-[#234333] border-[#234333]/30",
  CANCELLED: "bg-[#F1ECE0] text-[#5B5B5B] border-[#DDD3BA]",
  REJECTED: "bg-[#FDECEC] text-[#B02020] border-[#B02020]/30",
  FAILED_PAYMENT: "bg-[#FDECEC] text-[#B02020] border-[#B02020]/30",
};

function fmtINR(n) {
  const v = Number(n) || 0;
  return `₹ ${v.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}
function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); } catch { return s; }
}
function fmtDT(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return s; }
}

export default function AmenityBookings() {
  const { role } = useAuth();
  const canManage = ["super_admin", "editor", "infrastructure"].includes(role);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("ALL");
  const [scope, setScope] = useState("current");  // "current" | "past"
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [detail, setDetail] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  const [cancelFor, setCancelFor] = useState(null);
  const [refundFor, setRefundFor] = useState(null);
  const [refundForm, setRefundForm] = useState({ amount: "", method: "bank_transfer", reference: "", reason: "" });
  const [manualOpen, setManualOpen] = useState(false);
  const [editFor, setEditFor] = useState(null);
  const [approveFor, setApproveFor] = useState(null);
  const [approveForm, setApproveForm] = useState({ payment_method: "cash", payment_reference: "", payment_notes: "" });
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { scope };
      if (status !== "ALL") params.status = status;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await api.get("/admin/amenity-bookings", { params });
      setRows(data.items || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load bookings");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [status, dateFrom, dateTo, scope]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((r) =>
      [r.booking_ref, r.applicant_name, r.email, r.mobile, r.amenity_title, r.event_name, r.organization]
        .some((x) => (x || "").toString().toLowerCase().includes(term))
    );
  }, [rows, q]);

  const verify = async (ref) => {
    setBusy(true);
    try {
      await api.post(`/admin/amenity-bookings/${ref}/verify`);
      toast.success("Booking confirmed & receipt emailed");
      await load();
      if (detail?.booking_ref === ref) setDetail(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Verification failed");
    } finally { setBusy(false); }
  };

  const doReject = async () => {
    if (!rejectFor || !reason.trim()) return toast.error("Please provide a reason");
    setBusy(true);
    try {
      await api.post(`/admin/amenity-bookings/${rejectFor.booking_ref}/reject`, { reason: reason.trim() });
      toast.success("Booking rejected");
      setRejectFor(null); setReason("");
      await load();
      if (detail?.booking_ref === rejectFor.booking_ref) setDetail(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Reject failed");
    } finally { setBusy(false); }
  };

  const doCancel = async () => {
    if (!cancelFor) return;
    setBusy(true);
    try {
      await api.post(`/admin/amenity-bookings/${cancelFor.booking_ref}/cancel`);
      toast.success("Booking cancelled");
      setCancelFor(null);
      await load();
      if (detail?.booking_ref === cancelFor.booking_ref) setDetail(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Cancel failed");
    } finally { setBusy(false); }
  };

  const openRefund = (row) => {
    setRefundFor(row);
    const paid = Number(row.total_amount) || 0;
    const already = Number(row.refund_total) || 0;
    setRefundForm({
      amount: (paid - already).toFixed(2),
      method: "bank_transfer",
      reference: "",
      reason: "",
    });
  };

  const doRefund = async () => {
    if (!refundFor) return;
    const amount = Number(refundForm.amount);
    if (!amount || amount <= 0) return toast.error("Enter a valid refund amount");
    if (!refundForm.reason.trim()) return toast.error("Please provide a refund reason");
    setBusy(true);
    try {
      await api.post(`/admin/amenity-bookings/${refundFor.booking_ref}/refund`, {
        amount, method: refundForm.method, reference: refundForm.reference.trim(), reason: refundForm.reason.trim(),
      });
      toast.success("Refund recorded");
      setRefundFor(null);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Refund failed");
    } finally { setBusy(false); }
  };

  const openApprove = (row) => {
    setApproveFor(row);
    setApproveForm({ payment_method: "cash", payment_reference: "", payment_notes: "" });
  };

  const doApprove = async () => {
    if (!approveFor) return;
    setBusy(true);
    try {
      await api.post(`/admin/amenity-bookings/${approveFor.booking_ref}/record-payment`, {
        payment_method: approveForm.payment_method,
        payment_reference: approveForm.payment_reference.trim(),
        payment_notes: approveForm.payment_notes.trim(),
      });
      toast.success(approveForm.payment_method === "waived" ? "Booking approved (payment waived)" : "Booking approved & receipt emailed");
      setApproveFor(null);
      await load();
      if (detail?.booking_ref === approveFor.booking_ref) setDetail(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Approval failed");
    } finally { setBusy(false); }
  };

  const downloadReceipt = async (ref) => {
    try {
      const res = await api.get(`/admin/amenity-bookings/${ref}/receipt`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `Receipt-${ref}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Receipt download failed");
    }
  };

  const sendReminder = async (ref) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/amenity-bookings/${ref}/send-reminder`);
      if (data?.success) toast.success("Reminder email sent");
      else toast.warning("Reminder could not be delivered — check the applicant's email");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Reminder failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-amenity-bookings">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold">Infrastructure</div>
          <h1 className="font-serif text-2xl text-[#1c1a17]">Amenity Bookings</h1>
          <div className="text-sm text-[#6b655b] mt-0.5">Review applications, approve with payment details (or mark waived), reject, cancel, refund and enter walk-in bookings.</div>
        </div>
        {canManage && (
          <button type="button" onClick={() => setManualOpen(true)} data-testid="ab-new-manual" className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1a3326] text-white px-4 py-2.5 text-sm font-semibold rounded-sm">
            <Plus size={16} /> New Booking (walk-in)
          </button>
        )}
      </div>

      <div className="bg-white border border-[#e5e0d8] rounded-sm p-3 flex flex-wrap gap-2 items-center" data-testid="ab-filter-bar">
        <div className="inline-flex rounded-sm border border-[#e5e0d8] overflow-hidden" data-testid="ab-scope-switcher">
          <button
            type="button"
            onClick={() => setScope("current")}
            data-testid="ab-scope-current"
            className={`px-3 py-1.5 text-[11px] uppercase tracking-widest font-semibold transition ${
              scope === "current" ? "bg-[#234333] text-white" : "bg-white text-[#1c1a17] hover:bg-[#f5f1e8]"
            }`}
          >
            Current & Upcoming
          </button>
          <button
            type="button"
            onClick={() => setScope("past")}
            data-testid="ab-scope-past"
            className={`px-3 py-1.5 text-[11px] uppercase tracking-widest font-semibold transition border-l border-[#e5e0d8] ${
              scope === "past" ? "bg-[#234333] text-white" : "bg-white text-[#1c1a17] hover:bg-[#f5f1e8]"
            }`}
          >
            Past Events
          </button>
        </div>
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8a8478]" />
          <input className={`${inputCls} pl-8 w-full`} placeholder="Search ref, name, email, amenity, event…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="ab-search" />
        </div>
        <select className={inputCls} value={status} onChange={(e) => setStatus(e.target.value)} data-testid="ab-status">
          {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
        </select>
        <input type="date" className={inputCls} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="ab-date-from" title="Event date from" />
        <input type="date" className={inputCls} value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="ab-date-to" title="Event date to" />
        <button type="button" onClick={load} className="text-sm bg-[#234333] hover:bg-[#1a3326] text-white px-3 py-2 rounded-sm font-semibold" data-testid="ab-refresh">Refresh</button>
      </div>

      {loading ? (
        <div className="text-sm text-[#6b655b] flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-[#e5e0d8] rounded-sm p-10 text-center">
          <CalendarIcon size={36} className="mx-auto text-[#bcb6aa]" strokeWidth={1.2} />
          <div className="mt-3 text-sm text-[#6b655b]">No amenity bookings found for the selected filters.</div>
        </div>
      ) : (
        <div className="bg-white border border-[#e5e0d8] rounded-sm overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-[#f5f1e8] text-[#5b5650]">
              <tr>
                <Th>Ref</Th><Th>Amenity</Th><Th>Event date</Th><Th>Applicant</Th>
                <Th>Event</Th><Th>Total</Th><Th>Status</Th><Th>Created</Th><Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.booking_ref} className="border-t border-[#e5e0d8]" data-testid={`ab-row-${r.booking_ref}`}>
                  <Td><span className="font-mono text-xs">{r.booking_ref}</span></Td>
                  <Td>
                    <div className="font-semibold text-[#1c1a17]">{r.amenity_title}</div>
                    <div className="text-[10px] text-[#8a8478] uppercase tracking-widest">{r.is_stay || r.check_in_date ? `Stay · ${r.nights || 0} night${r.nights === 1 ? "" : "s"}` : r.duration}</div>
                  </Td>
                  {r.is_stay || r.check_in_date ? (
                    <Td>
                      <div>{fmtDate(r.check_in_date || r.event_date)}</div>
                      <div className="text-[10px] text-[#8a8478]">→ {fmtDate(r.check_out_date)} · 11:00 AM</div>
                    </Td>
                  ) : (
                    <Td>{fmtDate(r.event_date)}<div className="text-[10px] text-[#8a8478]">{r.time_slot || (r.start_time && `${r.start_time}${r.end_time ? "–" + r.end_time : ""}`)}</div></Td>
                  )}
                  <Td>
                    <div className="font-semibold">{r.applicant_name}</div>
                    <div className="text-[11px] text-[#8a8478]">{r.email}</div>
                    <div className="text-[11px] text-[#8a8478]">{r.mobile}</div>
                  </Td>
                  <Td className="max-w-[220px]"><div className="line-clamp-2">{r.event_name}</div><div className="text-[10px] text-[#8a8478]">{r.organization}</div></Td>
                  <Td className="font-semibold text-[#234333] whitespace-nowrap">{fmtINR(r.total_amount)}</Td>
                  <Td>
                    <span className={`inline-block text-[10px] uppercase tracking-widest font-bold border rounded px-2 py-0.5 ${STATUS_BADGE[r.status] || "bg-[#F1ECE0] text-[#5B5B5B] border-[#DDD3BA]"}`}>
                      {(r.status || "").replace("_", " ")}
                    </span>
                    {r.payment_method && <div className="text-[10px] text-[#8a8478] mt-0.5">{r.payment_method}</div>}
                    {r.refund_status && (
                      <div className="mt-1 inline-block text-[10px] uppercase tracking-widest font-bold border rounded px-1.5 py-0.5 bg-[#FEF7E4] text-[#8a5a1a] border-[#c78e42]/40">
                        {r.refund_status === "FULLY_REFUNDED" ? "Fully Refunded" : `Refunded ${fmtINR(r.refund_total)}`}
                      </div>
                    )}
                    {r.source === "manual" && <div className="mt-1 inline-block text-[10px] uppercase tracking-widest font-bold border rounded px-1.5 py-0.5 bg-[#E7F3F1] text-[#0D5C63] border-[#0D5C63]/30">Walk-in</div>}
                  </Td>
                  <Td className="text-xs text-[#8a8478] whitespace-nowrap">{fmtDT(r.created_at)}</Td>
                  <Td>
                    <div className="flex items-center gap-1">
                      <button type="button" onClick={() => setDetail(r)} data-testid={`ab-view-${r.booking_ref}`} className="text-[#234333] hover:bg-[#234333]/10 text-[11px] font-semibold px-2 py-1 rounded-sm inline-flex items-center gap-1"><Eye size={11} /> View</button>
                      {canManage && !["CANCELLED", "REJECTED"].includes(r.status) && (
                        <button type="button" onClick={() => setEditFor(r)} data-testid={`ab-edit-${r.booking_ref}`} className="text-[#234333] hover:bg-[#eef3ea] text-[11px] font-semibold px-2 py-1 rounded-sm inline-flex items-center gap-1"><Pencil size={11} /> Edit</button>
                      )}
                      {canManage && ["PROVISIONAL", "PENDING_VERIFICATION"].includes(r.status) && (
                        <button type="button" onClick={() => openApprove(r)} data-testid={`ab-approve-${r.booking_ref}`} className="text-[#0D5C63] hover:bg-[#E7F3F1] text-[11px] font-semibold px-2 py-1 rounded-sm inline-flex items-center gap-1"><CheckCircle2 size={11} /> Approve</button>
                      )}
                      {canManage && ["PROVISIONAL", "PENDING_VERIFICATION"].includes(r.status) && (
                        <button type="button" onClick={() => { setRejectFor(r); setReason(""); }} data-testid={`ab-reject-${r.booking_ref}`} className="text-red-700 hover:bg-red-50 text-[11px] font-semibold px-2 py-1 rounded-sm inline-flex items-center gap-1"><XCircle size={11} /> Reject</button>
                      )}
                      {canManage && ["PROVISIONAL", "CONFIRMED"].includes(r.status) && (
                        <button type="button" onClick={() => setCancelFor(r)} data-testid={`ab-cancel-${r.booking_ref}`} className="text-red-700 hover:bg-red-50 text-[11px] font-semibold px-2 py-1 rounded-sm inline-flex items-center gap-1"><Ban size={11} /> Cancel</button>
                      )}
                      {canManage && ["CONFIRMED", "CANCELLED"].includes(r.status) && Number(r.refund_total || 0) < Number(r.total_amount || 0) && (
                        <button type="button" onClick={() => openRefund(r)} data-testid={`ab-refund-${r.booking_ref}`} className="text-[#8a5a1a] hover:bg-[#FEF7E4] text-[11px] font-semibold px-2 py-1 rounded-sm inline-flex items-center gap-1"><Undo2 size={11} /> Refund</button>
                      )}
                      {r.status === "CONFIRMED" && (
                        <button type="button" onClick={() => downloadReceipt(r.booking_ref)} data-testid={`ab-receipt-${r.booking_ref}`} className="text-[#0D5C63] hover:bg-[#E7F3F1] text-[11px] font-semibold px-2 py-1 rounded-sm inline-flex items-center gap-1"><Download size={11} /> Receipt</button>
                      )}
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-[#fbf8f1] max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Booking Details</DialogTitle>
            <DialogDescription>Reference <span className="font-mono">{detail?.booking_ref}</span></DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-5 text-sm">
              <Section title="Amenity & Event">
                <Grid2>
                  <Info label="Amenity" value={detail.amenity_title} />
                  {detail.is_stay || detail.check_in_date ? (
                    <>
                      <Info label="Check-in" value={fmtDate(detail.check_in_date || detail.event_date)} />
                      <Info label="Check-out" value={`${fmtDate(detail.check_out_date)} · 11:00 AM`} />
                      <Info label="Nights" value={`${detail.nights || 0}`} />
                    </>
                  ) : (
                    <>
                      <Info label="Event Date" value={fmtDate(detail.event_date)} />
                      <Info label="Duration" value={detail.duration} />
                      <Info label="Time Slot" value={detail.time_slot || `${detail.start_time || "—"}–${detail.end_time || "—"}`} />
                    </>
                  )}
                  <Info label="Event Name" value={detail.event_name} full />
                  <Info label="Event Type" value={detail.event_type || "—"} />
                  <Info label="Expected Attendees" value={detail.attendees || "—"} />
                  <Info label="Purpose" value={detail.event_purpose || "—"} full />
                </Grid2>
              </Section>
              <Section title="Applicant">
                <Grid2>
                  <Info label="Name" value={detail.applicant_name} />
                  <Info label="Mobile" value={detail.mobile} />
                  <Info label="Email" value={detail.email} />
                  <Info label="Organisation" value={detail.organization || "—"} />
                </Grid2>
              </Section>
              <Section title="Facilities">
                <Grid2>
                  <Info label="Seating" value={detail.seating || "None"} />
                  <Info label="A-V Equipment" value={(detail.av_equipment || []).join(", ") || "None"} />
                  <Info label="Catering" value={(detail.catering_items || []).map((c) => `${c.name} (${fmtINR(c.price)})`).join(", ") || "None"} full />
                  <Info label="Additional Facilities" value={(detail.additional_facility_items || []).map((c) => `${c.name} (${fmtINR(c.price)})`).join(", ") || "None"} full />
                </Grid2>
              </Section>
              <Section title="Billing & Charges">
                <Grid2>
                  <Info label="Billing Name" value={detail.billing_name || detail.applicant_name} />
                  <Info label="GST No." value={detail.gst_number || "—"} />
                  <Info label="Billing Address" value={detail.billing_address || "—"} full />
                  <Info label="Base Charge" value={fmtINR(detail.base_charge)} />
                  <Info label="Add-ons" value={fmtINR(detail.additional_charge)} />
                  <Info label={`Tax (${detail.tax_percent || 0}%)`} value={fmtINR(detail.tax_amount)} />
                  <Info label="Security Deposit" value={fmtINR(detail.security_deposit)} />
                  <Info label="Total" value={<b className="text-[#0D5C63]">{fmtINR(detail.total_amount)}</b>} />
                </Grid2>
              </Section>
              <Section title="Payment">
                <Grid2>
                  <Info label="Status" value={detail.status} />
                  <Info label="Payment Method" value={detail.payment_method || "—"} />
                  <Info label="UTR / Tracking" value={detail.utr || detail.payment_tracking_id || "—"} />
                  <Info label="Confirmed At" value={fmtDT(detail.confirmed_at)} />
                  {detail.rejection_reason && <Info label="Rejection Reason" value={detail.rejection_reason} full />}
                  {detail.remarks && <Info label="Remarks" value={detail.remarks} full />}
                </Grid2>
              </Section>
              {(detail.refunds || []).length > 0 && (
                <Section title="Refund Trail">
                  <div className="text-xs">
                    <div className="grid grid-cols-[100px_100px_120px_1fr_100px] gap-2 font-bold text-[10px] uppercase tracking-widest text-[#8a8478] pb-2 border-b border-[#e5e0d8]">
                      <div>Date</div><div>Method</div><div>Reference</div><div>Reason</div><div className="text-right">Amount</div>
                    </div>
                    {(detail.refunds || []).map((r) => (
                      <div key={r.id} className="grid grid-cols-[100px_100px_120px_1fr_100px] gap-2 py-2 border-b border-[#f1ece0]" data-testid={`ab-refund-row-${r.id}`}>
                        <div>{fmtDate(r.recorded_at)}</div>
                        <div>{(r.method || "").replace("_", " ")}</div>
                        <div className="font-mono">{r.reference || "—"}</div>
                        <div>{r.reason}</div>
                        <div className="text-right font-semibold text-[#8a5a1a]">{fmtINR(r.amount)}</div>
                      </div>
                    ))}
                    <div className="grid grid-cols-[100px_100px_120px_1fr_100px] gap-2 py-2 mt-1 font-semibold">
                      <div /> <div /> <div /> <div className="text-right">Total Refunded</div>
                      <div className="text-right text-[#8a5a1a]">{fmtINR(detail.refund_total)}</div>
                    </div>
                    <div className="grid grid-cols-[100px_100px_120px_1fr_100px] gap-2 py-1 font-semibold text-[#0D5C63]">
                      <div /> <div /> <div /> <div className="text-right">Net Retained</div>
                      <div className="text-right">{fmtINR((Number(detail.total_amount)||0) - (Number(detail.refund_total)||0))}</div>
                    </div>
                  </div>
                </Section>
              )}
              {detail.source === "manual" && detail.manual_notes && (
                <Section title="Walk-in / Manual Booking">
                  <Info label="Recorded By" value={detail.created_by || "—"} />
                  <Info label="Notes" value={detail.manual_notes} full />
                </Section>
              )}
            </div>
          )}
          <DialogFooter>
            <button type="button" onClick={() => setDetail(null)} className="text-sm px-4 py-2.5 rounded-sm border border-[#e5e0d8] text-[#6b655b] hover:bg-[#f5f1e8]">Close</button>
            {detail?.status === "CONFIRMED" && (
              <>
                <button type="button" onClick={() => sendReminder(detail.booking_ref)} disabled={busy} data-testid="ab-send-reminder" className="text-sm bg-[#F26419] hover:bg-[#D90368] text-white px-4 py-2.5 rounded-sm font-semibold inline-flex items-center gap-2">
                  <CalendarIcon size={14} /> Send Reminder Now
                </button>
                <button type="button" onClick={() => downloadReceipt(detail.booking_ref)} className="text-sm bg-[#0D5C63] hover:bg-[#083E43] text-white px-4 py-2.5 rounded-sm font-semibold inline-flex items-center gap-2">
                  <Download size={14} /> Download Receipt
                </button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject reason */}
      <AlertDialog open={!!rejectFor} onOpenChange={(o) => !o && setRejectFor(null)}>
        <AlertDialogContent className="bg-[#fbf8f1]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl">Reject application?</AlertDialogTitle>
            <AlertDialogDescription>Please provide a reason. The applicant will not receive an automatic email; please follow up manually.</AlertDialogDescription>
          </AlertDialogHeader>
          <textarea
            rows={3}
            className={`${inputCls} w-full`}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="ab-reject-reason"
            placeholder="Reason for rejection (e.g. amount mismatch, invalid UTR, no credit received)…"
          />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doReject} data-testid="ab-reject-confirm" className="bg-red-700 hover:bg-red-800 text-white">Reject</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel */}
      <AlertDialog open={!!cancelFor} onOpenChange={(o) => !o && setCancelFor(null)}>
        <AlertDialogContent className="bg-[#fbf8f1]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl">Cancel this booking?</AlertDialogTitle>
            <AlertDialogDescription>This will mark <b>{cancelFor?.booking_ref}</b> as cancelled and free up the event date.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep booking</AlertDialogCancel>
            <AlertDialogAction onClick={doCancel} data-testid="ab-cancel-confirm" className="bg-red-700 hover:bg-red-800 text-white">Cancel booking</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Approve — record payment or mark waived */}
      <Dialog open={!!approveFor} onOpenChange={(o) => !o && setApproveFor(null)}>
        <DialogContent className="bg-[#fbf8f1] max-w-lg" data-testid="ab-approve-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl inline-flex items-center gap-2"><CheckCircle2 size={20} /> Approve Booking</DialogTitle>
            <DialogDescription>
              Record how payment was collected — or waive it — for booking <span className="font-mono">{approveFor?.booking_ref}</span> · <b>{approveFor?.applicant_name}</b> · Total <b>{fmtINR(approveFor?.total_amount)}</b>.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <label className="block">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-1.5">Payment Method *</div>
              <select className={`${inputCls} w-full`} value={approveForm.payment_method} onChange={(e) => setApproveForm((f) => ({ ...f, payment_method: e.target.value }))} data-testid="ab-approve-method">
                <option value="cash">Cash</option>
                <option value="cheque">Cheque</option>
                <option value="bank_transfer">Bank Transfer / NEFT</option>
                <option value="upi">UPI</option>
                <option value="manual">Other (Manual)</option>
                <option value="waived">Waived (no payment)</option>
              </select>
            </label>
            <label className="block">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-1.5">Reference (UTR / Cheque no. / Txn id)</div>
              <input type="text" className={`${inputCls} w-full`} value={approveForm.payment_reference} onChange={(e) => setApproveForm((f) => ({ ...f, payment_reference: e.target.value }))} data-testid="ab-approve-reference" />
            </label>
            <label className="block">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-1.5">Notes / Payment Details</div>
              <textarea rows={3} className={`${inputCls} w-full`} value={approveForm.payment_notes} onChange={(e) => setApproveForm((f) => ({ ...f, payment_notes: e.target.value }))} data-testid="ab-approve-notes" placeholder={approveForm.payment_method === "waived" ? "Reason for waiver (visible on receipt)" : "Any payment details, cheque bank, deposit slip #, etc."} />
            </label>
            <div className="bg-[#E7F3F1] border border-[#0D5C63]/25 rounded-md p-3 text-[11px] text-[#0D5C63] inline-flex items-start gap-2">
              <InfoIcon size={14} className="mt-0.5" />
              <div>Confirms the booking and emails a receipt to the applicant. Bookings marked <b>waived</b> are confirmed with ₹0 total.</div>
            </div>
          </div>
          <DialogFooter>
            <button type="button" onClick={() => setApproveFor(null)} className="text-sm px-4 py-2.5 rounded-sm border border-[#e5e0d8] text-[#6b655b] hover:bg-[#f5f1e8]">Cancel</button>
            <button type="button" onClick={doApprove} disabled={busy} data-testid="ab-approve-save" className="text-sm bg-[#0D5C63] hover:bg-[#083E43] text-white px-4 py-2.5 rounded-sm font-semibold inline-flex items-center gap-2">
              {busy ? <><Loader2 size={14} className="animate-spin" /> Approving…</> : <><CheckCircle2 size={14} /> Approve & Confirm</>}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refund */}
      <Dialog open={!!refundFor} onOpenChange={(o) => !o && setRefundFor(null)}>
        <DialogContent className="bg-[#fbf8f1] max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl inline-flex items-center gap-2"><Undo2 size={20} /> Record Refund</DialogTitle>
            <DialogDescription>
              Booking <span className="font-mono">{refundFor?.booking_ref}</span> · Paid <b>{fmtINR(refundFor?.total_amount)}</b>
              {refundFor?.refund_total > 0 && <> · Already refunded <b className="text-[#8a5a1a]">{fmtINR(refundFor?.refund_total)}</b></>}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <label className="block">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-1.5">Refund Amount (₹) *</div>
              <input type="number" min="1" step="0.01" className={`${inputCls} w-full`} value={refundForm.amount} onChange={(e) => setRefundForm((f) => ({ ...f, amount: e.target.value }))} data-testid="ab-refund-amount" />
            </label>
            <label className="block">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-1.5">Refund Method</div>
              <select className={`${inputCls} w-full`} value={refundForm.method} onChange={(e) => setRefundForm((f) => ({ ...f, method: e.target.value }))} data-testid="ab-refund-method">
                <option value="bank_transfer">Bank Transfer / NEFT</option>
                <option value="cash">Cash</option>
                <option value="cheque">Cheque</option>
                <option value="upi">UPI</option>
                <option value="gateway_reversal">Gateway Reversal</option>
              </select>
            </label>
            <label className="block">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-1.5">Reference (UTR / Cheque no. / Txn ID)</div>
              <input type="text" className={`${inputCls} w-full`} value={refundForm.reference} onChange={(e) => setRefundForm((f) => ({ ...f, reference: e.target.value }))} data-testid="ab-refund-reference" />
            </label>
            <label className="block">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-1.5">Reason *</div>
              <textarea rows={3} className={`${inputCls} w-full`} value={refundForm.reason} onChange={(e) => setRefundForm((f) => ({ ...f, reason: e.target.value }))} data-testid="ab-refund-reason" />
            </label>
          </div>
          <DialogFooter>
            <button type="button" onClick={() => setRefundFor(null)} className="text-sm px-4 py-2.5 rounded-sm border border-[#e5e0d8] text-[#6b655b] hover:bg-[#f5f1e8]">Cancel</button>
            <button type="button" onClick={doRefund} disabled={busy} data-testid="ab-refund-save" className="text-sm bg-[#8a5a1a] hover:bg-[#6b4515] text-white px-4 py-2.5 rounded-sm font-semibold inline-flex items-center gap-2">
              {busy ? <><Loader2 size={14} className="animate-spin" /> Recording…</> : <><IndianRupee size={14} /> Record Refund</>}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual walk-in booking */}
      <ManualBookingDialog
        open={manualOpen}
        onClose={() => setManualOpen(false)}
        onCreated={async () => { setManualOpen(false); await load(); }}
      />

      {/* Edit existing booking */}
      <EditBookingDialog
        booking={editFor}
        onClose={() => setEditFor(null)}
        onSaved={async () => { setEditFor(null); await load(); }}
      />
    </div>
  );
}

function Th({ children }) { return <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest font-bold">{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-3 py-3 align-top ${className}`}>{children}</td>; }
function Section({ title, children }) {
  return (
    <div className="bg-white border border-[#e5e0d8] rounded-sm p-4">
      <div className="text-[10px] tracking-widest uppercase text-[#F26419] font-bold mb-2">{title}</div>
      {children}
    </div>
  );
}
function Grid2({ children }) { return <div className="grid md:grid-cols-2 gap-x-6 gap-y-3">{children}</div>; }
function Info({ label, value, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <div className="text-[10px] tracking-widest uppercase text-[#8a8478] font-bold">{label}</div>
      <div className="text-[#1A1A1A] mt-0.5 break-words">{value}</div>
    </div>
  );
}

/* -------- Walk-in / Manual booking dialog (Infrastructure admin) -------- */
function ManualBookingDialog({ open, onClose, onCreated }) {
  const [amenities, setAmenities] = useState([]);
  const [amenity, setAmenity] = useState(null);
  const [form, setForm] = useState(EMPTY_MANUAL);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/admin/amenities");
        const enabled = (data || []).filter((a) => a.booking_enabled && a.status === "Active");
        setAmenities(enabled);
      } catch (err) {
        toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load amenities");
      }
    })();
    setForm(EMPTY_MANUAL);
    setAmenity(null);
  }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleIn = (k, name, on) => setForm((f) => {
    const cur = new Set(f[k] || []);
    if (on) cur.add(name); else cur.delete(name);
    return { ...f, [k]: Array.from(cur) };
  });

  // Auto-calc end_time = start_time + 4h (Half Day) or 8h (Full Day).
  useEffect(() => {
    const st = (form.start_time || "").trim();
    if (!/^\d{2}:\d{2}$/.test(st)) return;
    const [h, m] = st.split(":").map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return;
    const add = form.duration === "Half Day" ? 4 : 8;
    const total = h * 60 + m + add * 60;
    const eh = Math.floor((total / 60) % 24);
    const em = total % 60;
    const et = `${String(eh).padStart(2, "0")}:${String(em).padStart(2, "0")}`;
    if (et !== form.end_time) setForm((f) => ({ ...f, end_time: et }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.start_time, form.duration]);

  const isStay = !!amenity && ["Hostel", "Guest House"].includes(amenity.amenity_type);
  const nights = useMemo(() => {
    if (!isStay || !form.check_in_date || !form.check_out_date) return 0;
    const diff = Math.round((new Date(form.check_out_date) - new Date(form.check_in_date)) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  }, [isStay, form.check_in_date, form.check_out_date]);

  const pricing = useMemo(() => {
    if (!amenity) return null;
    let base;
    if (isStay) {
      base = Number(amenity.full_day_charge || 0) * nights;
    } else {
      base = form.duration === "Half Day" ? Number(amenity.half_day_charge || 0) : Number(amenity.full_day_charge || 0);
    }
    const additional = 0;
    const taxPct = Number(amenity.tax_percent || 0);
    const tax = Math.round((base + additional) * taxPct) / 100;
    const deposit = Number(amenity.security_deposit || 0);
    const total = Math.round((base + additional + tax + deposit) * 100) / 100;
    return { base, additional, tax, taxPct, deposit, total };
  }, [amenity, form.duration, isStay, nights]);

  const submit = async () => {
    if (!amenity) return toast.error("Please choose an amenity");
    if (isStay) {
      if (!form.check_in_date) return toast.error("Please choose check-in date");
      if (!form.check_out_date) return toast.error("Please choose check-out date");
      if (new Date(form.check_out_date) <= new Date(form.check_in_date)) return toast.error("Check-out must be after check-in");
      if (nights > 5) return toast.error("Maximum stay is 5 nights");
    } else {
      if (!form.event_date) return toast.error("Please choose the event date");
    }
    if (!form.applicant_name.trim()) return toast.error("Applicant name is required");
    if (!/^\+?\d[\d\s-]{5,}$/.test(form.mobile.trim())) return toast.error("Enter a valid mobile number");
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) return toast.error("Enter a valid email address");
    if (!form.event_name.trim()) return toast.error(isStay ? "Purpose of stay is required" : "Event name is required");
    setBusy(true);
    try {
      const payload = {
        ...form,
        amenity_id: amenity.id,
        attendees: Number(form.attendees) || 0,
        accept_terms: true,
        event_date: isStay ? form.check_in_date : form.event_date,
        check_in_date: isStay ? form.check_in_date : "",
        check_out_date: isStay ? form.check_out_date : "",
        duration: isStay ? "Full Day" : form.duration,
        start_time: isStay ? "" : form.start_time,
        end_time: isStay ? "" : form.end_time,
      };
      await api.post("/admin/amenity-bookings/manual", payload);
      toast.success("Walk-in booking confirmed & receipt emailed");
      onCreated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Booking failed");
    } finally { setBusy(false); }
  };

  const seats = amenity?.seating_options || [];
  const avs = amenity?.av_options || [];
  const caterings = [];
  const addfacs = [];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#fbf8f1] max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">New Walk-in Booking</DialogTitle>
          <DialogDescription>
            Enter a booking on behalf of a walk-in / offline customer. Confirms immediately with the payment method you record — no gateway involved.
          </DialogDescription>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-4">
          <label className="block md:col-span-2">
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-1.5">Amenity *</div>
            <select className={`${inputCls} w-full`} value={amenity?.id || ""} onChange={(e) => setAmenity(amenities.find((a) => a.id === e.target.value) || null)} data-testid="ab-manual-amenity">
              <option value="">Choose an amenity</option>
              {amenities.map((a) => <option key={a.id} value={a.id}>{a.title} · {a.amenity_type}</option>)}
            </select>
          </label>
          {isStay ? (
            <>
              <MField label="Check-in Date *"><input type="date" className={`${inputCls} w-full`} value={form.check_in_date} onChange={(e) => set("check_in_date", e.target.value)} data-testid="ab-manual-checkin" /></MField>
              <MField label="Check-out Date *"><input type="date" className={`${inputCls} w-full`} value={form.check_out_date} min={form.check_in_date || ""} onChange={(e) => set("check_out_date", e.target.value)} data-testid="ab-manual-checkout" /></MField>
              <div className="md:col-span-2 inline-flex items-start gap-2 rounded bg-[#FEF7E4] border border-[#c78e42]/40 px-3 py-2 text-[11px] text-[#8a5a1a] font-semibold" data-testid="ab-manual-stay-note">
                <InfoIcon size={12} className="mt-0.5 shrink-0" />
                <span>Nights: <b className="text-[#0D5C63]">{nights}</b> · Checkout time is 11:00 AM · Max stay 5 nights.</span>
              </div>
            </>
          ) : (
            <>
              <MField label="Event Date *"><input type="date" className={`${inputCls} w-full`} value={form.event_date} onChange={(e) => set("event_date", e.target.value)} data-testid="ab-manual-date" /></MField>
              <MField label="Duration *">
                <select className={`${inputCls} w-full`} value={form.duration} onChange={(e) => set("duration", e.target.value)} data-testid="ab-manual-duration">
                  <option value="Half Day">Half Day</option>
                  <option value="Full Day">Full Day</option>
                </select>
                <div className="mt-1.5 inline-flex items-center gap-1 text-[10px] text-[#234333] font-semibold bg-[#eef3ea] border border-[#234333]/25 rounded px-2 py-0.5" data-testid="ab-manual-duration-hint">
                  <InfoIcon size={10} /> {form.duration === "Half Day" ? "Allowed for 4 hours" : "Allowed for 8 hours"}
                </div>
              </MField>
              <MField label="Start Time"><input type="time" className={`${inputCls} w-full`} value={form.start_time} onChange={(e) => set("start_time", e.target.value)} data-testid="ab-manual-start" /></MField>
              <MField label="End Time (auto-calculated)"><input type="time" className={`${inputCls} w-full bg-[#f5f1e8] cursor-not-allowed`} value={form.end_time} readOnly data-testid="ab-manual-end" /></MField>
            </>
          )}
        </div>

        <div className="mt-4 border-t border-[#e5e0d8] pt-4 grid md:grid-cols-2 gap-4">
          <MField label="Applicant Name *"><input type="text" className={`${inputCls} w-full`} value={form.applicant_name} onChange={(e) => set("applicant_name", e.target.value)} data-testid="ab-manual-name" /></MField>
          <MField label="Mobile *"><input type="tel" className={`${inputCls} w-full`} value={form.mobile} onChange={(e) => set("mobile", e.target.value)} data-testid="ab-manual-mobile" /></MField>
          <MField label="Email *"><input type="email" className={`${inputCls} w-full`} value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="ab-manual-email" /></MField>
          <MField label="Organisation"><input type="text" className={`${inputCls} w-full`} value={form.organization} onChange={(e) => set("organization", e.target.value)} /></MField>
          <MField label="Event Name *"><input type="text" className={`${inputCls} w-full`} value={form.event_name} onChange={(e) => set("event_name", e.target.value)} data-testid="ab-manual-event" /></MField>
          <MField label="Event Type"><input type="text" className={`${inputCls} w-full`} value={form.event_type} onChange={(e) => set("event_type", e.target.value)} /></MField>
          <MField label="Attendees"><input type="number" min="0" className={`${inputCls} w-full`} value={form.attendees} onChange={(e) => set("attendees", e.target.value)} /></MField>
          <MField label="Purpose" full><textarea rows={2} className={`${inputCls} w-full`} value={form.event_purpose} onChange={(e) => set("event_purpose", e.target.value)} /></MField>
        </div>

        {amenity && (seats.length > 0 || avs.length > 0 || caterings.length > 0 || addfacs.length > 0) && (
          <div className="mt-4 border-t border-[#e5e0d8] pt-4 grid gap-3">
            {seats.length > 0 && (
              <MField label="Seating">
                <select className={`${inputCls} w-full`} value={form.seating} onChange={(e) => set("seating", e.target.value)}>
                  <option value="">Not required</option>
                  {seats.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </MField>
            )}
            {avs.length > 0 && (
              <MField label="A-V Equipment">
                <div className="grid sm:grid-cols-2 gap-1.5 text-sm">
                  {avs.map((n) => (
                    <label key={n} className="inline-flex items-center gap-2">
                      <input type="checkbox" className="accent-[#234333]" checked={(form.av_equipment || []).includes(n)} onChange={(e) => toggleIn("av_equipment", n, e.target.checked)} />
                      {n}
                    </label>
                  ))}
                </div>
              </MField>
            )}
            {caterings.length > 0 && (
              <MField label="Catering (paid)">
                <div className="grid sm:grid-cols-2 gap-1.5 text-sm">
                  {caterings.map((o) => (
                    <label key={o.name} className="inline-flex items-center gap-2 justify-between border border-[#e5e0d8] px-2 py-1 rounded">
                      <span className="inline-flex items-center gap-2">
                        <input type="checkbox" className="accent-[#234333]" checked={(form.catering || []).includes(o.name)} onChange={(e) => toggleIn("catering", o.name, e.target.checked)} />
                        {o.name}
                      </span>
                      <span className="text-xs text-[#0D5C63] font-semibold">{fmtINR(o.price)}</span>
                    </label>
                  ))}
                </div>
              </MField>
            )}
            {addfacs.length > 0 && (
              <MField label="Additional Facilities (paid)">
                <div className="grid sm:grid-cols-2 gap-1.5 text-sm">
                  {addfacs.map((o) => (
                    <label key={o.name} className="inline-flex items-center gap-2 justify-between border border-[#e5e0d8] px-2 py-1 rounded">
                      <span className="inline-flex items-center gap-2">
                        <input type="checkbox" className="accent-[#234333]" checked={(form.additional_facilities || []).includes(o.name)} onChange={(e) => toggleIn("additional_facilities", o.name, e.target.checked)} />
                        {o.name}
                      </span>
                      <span className="text-xs text-[#0D5C63] font-semibold">{fmtINR(o.price)}</span>
                    </label>
                  ))}
                </div>
              </MField>
            )}
          </div>
        )}

        <div className="mt-4 border-t border-[#e5e0d8] pt-4 grid md:grid-cols-2 gap-4">
          <MField label="Billing Name"><input type="text" className={`${inputCls} w-full`} value={form.billing_name} onChange={(e) => set("billing_name", e.target.value)} placeholder="Defaults to applicant name" /></MField>
          <MField label="GST No."><input type="text" className={`${inputCls} w-full`} value={form.gst_number} onChange={(e) => set("gst_number", e.target.value)} /></MField>
          <MField label="Billing Address" full><textarea rows={2} className={`${inputCls} w-full`} value={form.billing_address} onChange={(e) => set("billing_address", e.target.value)} /></MField>
          <MField label="Remarks" full><textarea rows={2} className={`${inputCls} w-full`} value={form.remarks} onChange={(e) => set("remarks", e.target.value)} /></MField>
        </div>

        <div className="mt-4 border-t border-[#e5e0d8] pt-4 grid md:grid-cols-3 gap-4">
          <MField label="Payment Method *">
            <select className={`${inputCls} w-full`} value={form.payment_method} onChange={(e) => set("payment_method", e.target.value)} data-testid="ab-manual-method">
              <option value="cash">Cash</option>
              <option value="cheque">Cheque</option>
              <option value="bank_transfer">Bank Transfer / NEFT</option>
              <option value="manual">Other (Manual)</option>
              <option value="waived">Waived</option>
            </select>
          </MField>
          <MField label="Reference (UTR / Cheque No.)"><input type="text" className={`${inputCls} w-full`} value={form.payment_reference} onChange={(e) => set("payment_reference", e.target.value)} data-testid="ab-manual-reference" /></MField>
          <MField label="Notes"><input type="text" className={`${inputCls} w-full`} value={form.payment_notes} onChange={(e) => set("payment_notes", e.target.value)} /></MField>
          <MField label="Waiver Voucher (optional)" full>
            <input type="text" className={`${inputCls} w-full font-mono uppercase`} placeholder="GSAM-XXXX-XXXX-XX" value={form.voucher_code} onChange={(e) => set("voucher_code", e.target.value.toUpperCase())} data-testid="ab-manual-voucher" maxLength={32} />
            <div className="mt-1 text-[10px] text-[#8a8478]">If provided, the voucher fully waives all charges and payment method is set to <b>voucher_waived</b>.</div>
          </MField>
        </div>

        {pricing && (
          <div className="mt-4 bg-[#faf8f2] border border-[#e5e0d8] rounded-sm p-4 text-sm">
            <div className="grid md:grid-cols-2 gap-2">
              <div>{isStay ? `Stay (${nights} × ${fmtINR(amenity?.full_day_charge || 0)}/night)` : `Base (${form.duration})`} <b className="ml-1">{fmtINR(pricing.base)}</b></div>
              <div>Additional <b className="ml-1">{fmtINR(pricing.additional)}</b></div>
              <div>Tax ({pricing.taxPct}%) <b className="ml-1">{fmtINR(pricing.tax)}</b></div>
              <div>Security deposit <b className="ml-1">{fmtINR(pricing.deposit)}</b></div>
            </div>
            <div className="mt-2 border-t border-[#e5e0d8] pt-2 flex justify-between items-center">
              <div className="text-[10px] uppercase tracking-widest text-[#8a8478] font-bold">Total Payable</div>
              <div className="font-serif text-2xl text-[#0D5C63]" data-testid="ab-manual-total">{fmtINR(pricing.total)}</div>
            </div>
          </div>
        )}

        <DialogFooter>
          <button type="button" onClick={onClose} className="text-sm px-4 py-2.5 rounded-sm border border-[#e5e0d8] text-[#6b655b] hover:bg-[#f5f1e8]">Cancel</button>
          <button type="button" onClick={submit} disabled={busy} data-testid="ab-manual-submit" className="text-sm bg-[#234333] hover:bg-[#1a3326] text-white px-4 py-2.5 rounded-sm font-semibold inline-flex items-center gap-2">
            {busy ? <><Loader2 size={14} className="animate-spin" /> Confirming…</> : <><CheckCircle2 size={14} /> Confirm Booking</>}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const EMPTY_MANUAL = {
  event_date: "",
  check_in_date: "",
  check_out_date: "",
  duration: "Half Day",
  time_slot: "",
  start_time: "",
  end_time: "",
  applicant_name: "",
  mobile: "",
  email: "",
  organization: "",
  event_name: "",
  event_type: "",
  event_purpose: "",
  attendees: "",
  seating: "",
  av_equipment: [],
  catering: [],
  additional_facilities: [],
  billing_name: "",
  billing_address: "",
  gst_number: "",
  remarks: "",
  payment_method: "cash",
  payment_reference: "",
  payment_notes: "",
  voucher_code: "",
};

function MField({ label, children, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-1.5">{label}</div>
      {children}
    </label>
  );
}

/* ----------- Edit an existing booking (Infrastructure admin) ------------ */
function EditBookingDialog({ booking, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const isStay = !!booking && (booking.is_stay || ["Hostel", "Guest House"].includes(booking.amenity_type || ""));

  useEffect(() => {
    if (!booking) { setForm(null); return; }
    setForm({
      event_date: booking.event_date || "",
      check_in_date: booking.check_in_date || "",
      check_out_date: booking.check_out_date || "",
      duration: booking.duration || "Half Day",
      start_time: booking.start_time || "",
      end_time: booking.end_time || "",
      applicant_name: booking.applicant_name || "",
      mobile: booking.mobile || "",
      email: booking.email || "",
      organization: booking.organization || "",
      event_name: booking.event_name || "",
      event_type: booking.event_type || "",
      event_purpose: booking.event_purpose || "",
      attendees: booking.attendees || 0,
      seating: booking.seating || "",
      av_equipment: Array.isArray(booking.av_equipment) ? booking.av_equipment.join(", ") : "",
      billing_name: booking.billing_name || "",
      billing_address: booking.billing_address || "",
      gst_number: booking.gst_number || "",
      remarks: booking.remarks || "",
    });
  }, [booking]);

  // Auto-calc end_time = start_time + duration hours.
  useEffect(() => {
    if (!form) return;
    const st = (form.start_time || "").trim();
    if (!/^\d{2}:\d{2}$/.test(st)) return;
    const [h, m] = st.split(":").map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return;
    const add = form.duration === "Half Day" ? 4 : 8;
    const total = h * 60 + m + add * 60;
    const eh = Math.floor((total / 60) % 24);
    const em = total % 60;
    const et = `${String(eh).padStart(2, "0")}:${String(em).padStart(2, "0")}`;
    if (et !== form.end_time) setForm((f) => (f ? { ...f, end_time: et } : f));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form?.start_time, form?.duration]);

  if (!form) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (isStay) {
      if (!form.check_in_date) return toast.error("Please choose check-in date");
      if (!form.check_out_date) return toast.error("Please choose check-out date");
      if (new Date(form.check_out_date) <= new Date(form.check_in_date)) return toast.error("Check-out must be after check-in");
      const nights = Math.round((new Date(form.check_out_date) - new Date(form.check_in_date)) / (1000 * 60 * 60 * 24));
      if (nights > 5) return toast.error("Maximum stay is 5 nights");
    }
    setBusy(true);
    try {
      const payload = {
        event_date: form.event_date,
        check_in_date: isStay ? form.check_in_date : "",
        check_out_date: isStay ? form.check_out_date : "",
        duration: isStay ? "Stay" : form.duration,
        start_time: isStay ? "" : form.start_time,
        end_time: isStay ? "" : form.end_time,
        applicant_name: form.applicant_name.trim(),
        mobile: form.mobile.trim(),
        email: form.email.trim(),
        organization: form.organization.trim(),
        event_name: form.event_name.trim(),
        event_type: form.event_type.trim(),
        event_purpose: form.event_purpose.trim(),
        attendees: Number(form.attendees) || 0,
        seating: form.seating,
        av_equipment: (form.av_equipment || "").split(",").map((s) => s.trim()).filter(Boolean),
        billing_name: form.billing_name.trim(),
        billing_address: form.billing_address.trim(),
        gst_number: form.gst_number.trim(),
        remarks: form.remarks.trim(),
      };
      await api.patch(`/admin/amenity-bookings/${booking.booking_ref}`, payload);
      toast.success("Booking updated");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Update failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!booking} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#fbf8f1] max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="ab-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Edit Booking</DialogTitle>
          <DialogDescription>
            Reference <span className="font-mono">{booking.booking_ref}</span> · Amenity <b>{booking.amenity_title}</b> (cannot be changed)
          </DialogDescription>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-4">
          {isStay ? (
            <>
              <MField label="Check-in Date *"><input type="date" className={`${inputCls} w-full`} value={form.check_in_date} onChange={(e) => set("check_in_date", e.target.value)} data-testid="ab-edit-checkin" /></MField>
              <MField label="Check-out Date *"><input type="date" className={`${inputCls} w-full`} value={form.check_out_date} min={form.check_in_date || ""} onChange={(e) => set("check_out_date", e.target.value)} data-testid="ab-edit-checkout" /></MField>
              <div className="md:col-span-2 inline-flex items-start gap-2 rounded bg-[#FEF7E4] border border-[#c78e42]/40 px-3 py-2 text-[11px] text-[#8a5a1a] font-semibold">
                <InfoIcon size={12} className="mt-0.5 shrink-0" />
                <span>Checkout time is 11:00 AM · Maximum stay 5 nights.</span>
              </div>
            </>
          ) : (
            <>
              <MField label="Event Date *"><input type="date" className={`${inputCls} w-full`} value={form.event_date} onChange={(e) => set("event_date", e.target.value)} data-testid="ab-edit-date" /></MField>
              <MField label="Duration *">
                <select className={`${inputCls} w-full`} value={form.duration} onChange={(e) => set("duration", e.target.value)} data-testid="ab-edit-duration">
                  <option value="Half Day">Half Day</option>
                  <option value="Full Day">Full Day</option>
                </select>
                <div className="mt-1.5 inline-flex items-center gap-1 text-[10px] text-[#234333] font-semibold bg-[#eef3ea] border border-[#234333]/25 rounded px-2 py-0.5">
                  <InfoIcon size={10} /> {form.duration === "Half Day" ? "Allowed for 4 hours" : "Allowed for 8 hours"}
                </div>
              </MField>
              <MField label="Start Time"><input type="time" className={`${inputCls} w-full`} value={form.start_time} onChange={(e) => set("start_time", e.target.value)} data-testid="ab-edit-start" /></MField>
              <MField label="End Time (auto-calculated)"><input type="time" className={`${inputCls} w-full bg-[#f5f1e8] cursor-not-allowed`} value={form.end_time} readOnly data-testid="ab-edit-end" /></MField>
            </>
          )}
        </div>

        <div className="mt-4 border-t border-[#e5e0d8] pt-4 grid md:grid-cols-2 gap-4">
          <MField label="Applicant Name *"><input type="text" className={`${inputCls} w-full`} value={form.applicant_name} onChange={(e) => set("applicant_name", e.target.value)} data-testid="ab-edit-name" /></MField>
          <MField label="Mobile *"><input type="tel" className={`${inputCls} w-full`} value={form.mobile} onChange={(e) => set("mobile", e.target.value)} data-testid="ab-edit-mobile" /></MField>
          <MField label="Email *"><input type="email" className={`${inputCls} w-full`} value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="ab-edit-email" /></MField>
          <MField label="Organisation"><input type="text" className={`${inputCls} w-full`} value={form.organization} onChange={(e) => set("organization", e.target.value)} /></MField>
          <MField label="Event Name *"><input type="text" className={`${inputCls} w-full`} value={form.event_name} onChange={(e) => set("event_name", e.target.value)} data-testid="ab-edit-event" /></MField>
          <MField label="Event Type"><input type="text" className={`${inputCls} w-full`} value={form.event_type} onChange={(e) => set("event_type", e.target.value)} /></MField>
          <MField label="Attendees"><input type="number" min="0" className={`${inputCls} w-full`} value={form.attendees} onChange={(e) => set("attendees", e.target.value)} /></MField>
          <MField label="Purpose" full><textarea rows={2} className={`${inputCls} w-full`} value={form.event_purpose} onChange={(e) => set("event_purpose", e.target.value)} /></MField>
        </div>

        <div className="mt-4 border-t border-[#e5e0d8] pt-4 grid md:grid-cols-2 gap-4">
          <MField label="Seating"><input type="text" className={`${inputCls} w-full`} value={form.seating} onChange={(e) => set("seating", e.target.value)} /></MField>
          <MField label="A-V Equipment (comma separated)"><input type="text" className={`${inputCls} w-full`} value={form.av_equipment} onChange={(e) => set("av_equipment", e.target.value)} placeholder="Projector, Wireless mic" /></MField>
          <MField label="Billing Name"><input type="text" className={`${inputCls} w-full`} value={form.billing_name} onChange={(e) => set("billing_name", e.target.value)} /></MField>
          <MField label="GST No."><input type="text" className={`${inputCls} w-full`} value={form.gst_number} onChange={(e) => set("gst_number", e.target.value)} /></MField>
          <MField label="Billing Address" full><textarea rows={2} className={`${inputCls} w-full`} value={form.billing_address} onChange={(e) => set("billing_address", e.target.value)} /></MField>
          <MField label="Remarks" full><textarea rows={2} className={`${inputCls} w-full`} value={form.remarks} onChange={(e) => set("remarks", e.target.value)} /></MField>
        </div>

        <div className="mt-4 bg-[#faf8f2] border border-[#e5e0d8] rounded-sm p-3 text-[11px] text-[#8a8478] inline-flex items-start gap-2">
          <InfoIcon size={12} className="mt-0.5" />
          <div>Changing the duration will recalculate the total. If a waiver voucher was applied earlier, the waiver is extended to the new total automatically. Amenity, payment status and refund trail are unchanged.</div>
        </div>

        <DialogFooter>
          <button type="button" onClick={onClose} className="text-sm px-4 py-2.5 rounded-sm border border-[#e5e0d8] text-[#6b655b] hover:bg-[#f5f1e8]">Cancel</button>
          <button type="button" onClick={submit} disabled={busy} data-testid="ab-edit-save" className="text-sm bg-[#234333] hover:bg-[#1a3326] text-white px-4 py-2.5 rounded-sm font-semibold inline-flex items-center gap-2">
            {busy ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : <><CheckCircle2 size={14} /> Save Changes</>}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
